import {AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {MatTable, MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {TableFilterManager} from "./TableFilterManager";
import {EventHandler} from "./EventHandler";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {DataObject} from "../../model/DataObject";

export interface SortLevel {
  columnDef: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})

export class TableComponent<T extends DataObject > implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  @Input() columns: any[];
  @Input() eventHandler: EventHandler;
  @Input() data: Array<T>;
  @Input() expandedTableRowTemplate: TemplateRef<HTMLElement>;
  @Input() dataServiceStatus: string;
  @Input() showFilters: boolean = true;

  displayedColumns = [];
  displayedColumnsFilters = [];

  dataSource: MatTableDataSource<T>;
  tableFilterManager: TableFilterManager<T>;

  @ViewChild(MatTable, { static: true }) table: MatTable<any>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  selected: T;
  objects: T[];

  expandedElement: any;

  multiSortLevels: SortLevel[] = [];


  constructor() {
  }

  isExpansionDetailRow = (i: number, row: Object) => row.hasOwnProperty('detailRow');
  // isExpansionDetailRow = (i: number, row: Object) => row.hasOwnProperty('detailRow');
  // isExpansionDetailRow(i: number, row: Object): boolean {
  //   return true;
  //   // return row.hasOwnProperty('detailRow');
  // }

  ngOnInit() {
  }
  ngOnChanges() {
      this.displayedColumns = this.columns.map(c => c.columnDef);
      this.displayedColumnsFilters = this.columns.map(c => c.columnDef + 'Filter');
      this.dataSource = new MatTableDataSource<T>(this.data);
      this.tableFilterManager = new TableFilterManager<T>(this.dataSource);

      const _this = this;
      this.dataSource.filterPredicate =
        (data: T, filter: string) => {
          return _this.tableFilterManager.tableFilter.match(data, filter);
        };
      this.dataSource.sortData = (items: T[], sort: MatSort): T[] => {
        return this.multiSortData(items);
      };
  }

  ngAfterViewInit(): void {
    if ( this.sort ) {
      this.dataSource.sort = this.sort;
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toUpperCase();
    console.log(`filter changed to ${this.dataSource.filter}`);
  }

  filterBySubject() {
    const filterFunction = (data: T, filter: any): boolean => {
      if (filter) {
        // const subjects = data.subjects;
        if ( data['ID']) {
          const value = '' + data['ID'];
          if ( value.indexOf(filter) !== -1) {
              return true;
          }
          return false;
        }
      } else {
        return true;
      }
    };
    return filterFunction;
  }

  /** Cycle sort for a column: none → asc → desc → none. Most recently clicked becomes primary sort. */
  toggleColumnSort(columnDef: string) {
    const existingIndex = this.multiSortLevels.findIndex(s => s.columnDef === columnDef);
    if (existingIndex === -1) {
      // Not sorted → add as ascending at the front (primary)
      this.multiSortLevels.unshift({ columnDef, direction: 'asc' });
    } else {
      const existing = this.multiSortLevels[existingIndex];
      if (existing.direction === 'asc') {
        // asc → desc, move to front
        this.multiSortLevels.splice(existingIndex, 1);
        this.multiSortLevels.unshift({ columnDef, direction: 'desc' });
      } else {
        // desc → remove (no sort)
        this.multiSortLevels.splice(existingIndex, 1);
      }
    }
    this.applySortChange();
  }

  getSortDirection(columnDef: string): 'asc' | 'desc' | '' {
    const level = this.multiSortLevels.find(s => s.columnDef === columnDef);
    return level ? level.direction : '';
  }

  getSortPriority(columnDef: string): number {
    const index = this.multiSortLevels.findIndex(s => s.columnDef === columnDef);
    return index === -1 ? -1 : index + 1;
  }

  private applySortChange() {
    // Force MatTableDataSource to re-sort by re-assigning the data
    // (sortChange.emit alone doesn't reliably trigger re-sort in all Material versions)
    const sorted = this.multiSortData([...this.dataSource.data]);
    this.dataSource.data = sorted;
    if (this.table) {
      this.table.renderRows();
    }
  }

  private multiSortData(items: T[]): T[] {
    if (this.multiSortLevels.length === 0) {
      return items;
    }

    const columnMap = new Map<string, any>();
    for (const col of this.columns) {
      columnMap.set(col.columnDef, col);
    }

    return [...items].sort((a: T, b: T) => {
      for (const level of this.multiSortLevels) {
        const col = columnMap.get(level.columnDef);
        let valA: any;
        let valB: any;

        if (col && col.cell) {
          valA = col.cell(a);
          valB = col.cell(b);
        } else if (col && col.property) {
          valA = a[col.property];
          valB = b[col.property];
        } else {
          valA = a[level.columnDef];
          valB = b[level.columnDef];
        }

        let result = 0;
        if (valA == null && valB == null) {
          result = 0;
        } else if (valA == null) {
          result = -1;
        } else if (valB == null) {
          result = 1;
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          result = valA - valB;
        } else {
          result = ('' + valA).localeCompare('' + valB);
        }

        if (result !== 0) {
          return result * (level.direction === 'asc' ? 1 : -1);
        }
      }
      return 0;
    });
  }

  sortData() {
    // Legacy — kept for compatibility but multiSortData is now used
    return (items: T[], sort: MatSort): T[] => {
      return this.multiSortData(items);
    };
  }

    poke() {
      console.log('poke');
  }

  onSelect(obj: T): void {
    this.selected = obj;
  }

  ngOnDestroy(): void {
  }

  getDataSourceData(): T[]  {
    return this.dataSource.data;
  }

  addItem(item: T) {
    if ( !this.contains(item, this.dataSource.data)) {
      this.dataSource.data.push(item);
      this.dataSource.sort = this.sort;
      // this.computeCumulative();
      this.dataSource.filter = '' + Math.random();
      if ( this.table ) {
        this.table.renderRows();
      }
    }
  }

  removeItem(item: T) {
    const foundIndex = this.dataSource.data.findIndex(i => i.getId() === item.getId());
    if (foundIndex > -1) {
      this.dataSource.data.splice(foundIndex, 1);
      this.dataSource.sort = this.sort;
      // this.computeCumulative();
      this.dataSource.filter = '' + Math.random();
      if ( this.table ) {
        this.table.renderRows();
      }
    }
  }

  contains(object: T, array: T[] ): boolean {
    for ( const o of array ) {
      if ( o.getId() === object.getId() ) {
        return true;
      }
    }
    return false;
  }

}
